import { createContext, useContext, useEffect, useState } from "react";
import client from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // CRITICAL: If returning from Google OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        const t = localStorage.getItem("rf_token");
        if (!t) { setLoading(false); return; }
        client.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
            localStorage.removeItem("rf_token");
        }).finally(() => setLoading(false));
    }, []);

    const login = async (email, password) => {
        const r = await client.post("/auth/login", { email, password });
        localStorage.setItem("rf_token", r.data.token);
        setUser(r.data.user);
        return r.data.user;
    };

    const register = async (name, email, password, referralCode, inviteToken) => {
        const r = await client.post("/auth/register", {
            name, email, password,
            referral_code: referralCode || null,
            invite_token: inviteToken || null,
        });
        localStorage.setItem("rf_token", r.data.token);
        setUser(r.data.user);
        return r.data.user;
    };

    const refresh = async () => {
        try {
            const r = await client.get("/auth/me");
            setUser(r.data);
            return r.data;
        } catch {
            return null;
        }
    };

    // Used by the password-reset flow: swap in the fresh JWT + user in one shot.
    const hydrateFromToken = async (token, userObj) => {
        localStorage.setItem("rf_token", token);
        if (userObj) {
            setUser(userObj);
            return userObj;
        }
        return refresh();
    };

    const logout = () => {
        // Also invalidate any Google OAuth session server-side (fire & forget).
        client.post("/auth/logout", {}, { withCredentials: true }).catch(() => {});
        localStorage.removeItem("rf_token");
        setUser(null);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, hydrateFromToken }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
